import { useEffect } from 'react';
import { styled } from 'styled-components';

import { visuallyHiddenCss } from '#/components/VisuallyHidden/VisuallyHidden';
import { useI18n } from '#/i18n/hooks/useI18n';
import { SearchResultsBody } from '#/modules/search/components/Body/SearchResultsBody';
import { SearchResultsHeader } from '#/modules/search/components/Results/Header/SearchResultsHeader';
import { SearchResultsHits } from '#/modules/search/components/Results/Hits/SearchResultsHits';
import {
  useSearchCurrentPageResults,
  useSearchLocation,
  useSearchSort,
} from '#/modules/search/contexts/SearchContext';
import { useSaveSearch } from '#/modules/search/hooks/useSaveSearch';
import { useSearchAnalytics } from '#/modules/search/hooks/useSearchAnalytics';
import { useSearchPageActions } from '#/modules/search/hooks/useSearchPageActions';
import type { SearchFacet } from '#/modules/search/types/SearchFacet';
import type { SearchType } from '#/modules/search/types/SearchType';
import {
  useSearchSavedListingsStore,
  useSearchSavedListingsSyncAlgoliaHits,
} from '#/modules/search/zustand-stores/searchSavedListingsStore';
import { useSavedSearchesActions } from '#/modules/userDashboard/contexts/SavedSearchesContext';
import { LISTING_TYPE_PLURAL_HUMAN_NAME_MAP_TITLE } from '#/utils/constants/general/listingTypePluralHumanNameMapTitle';

import { useSearchPageLifecycle } from './useSearchPageLifecycle';

const SearchPageTitle = styled.h1`
  ${visuallyHiddenCss}
`;

type Props = {
  searchType: SearchType;
  searchFacets: SearchFacet[];
  showSortOptions: boolean;
};

export function SearchPage({
  searchType,
  searchFacets,
  showSortOptions,
}: Props) {
  const { i18n } = useI18n();
  const { saveSearch } = useSaveSearch();
  const { setSearchUrlPageIndex, setSearchUrlSort } = useSearchPageActions({
    isSearchLandingPage: false,
  });
  const searchLocation = useSearchLocation();
  const sort = useSearchSort();

  const { getSearchTrackingData } = useSearchAnalytics();

  useSearchPageLifecycle({
    searchType,
    getSearchTrackingData,
    searchFacets,
  });

  const results = useSearchCurrentPageResults();

  const { searchSavedListingsStore } = useSearchSavedListingsStore();
  useSearchSavedListingsSyncAlgoliaHits({
    hits: results?.hits,
    searchSavedListingsStore,
  });

  const { dismissRecentlySavedSearch } = useSavedSearchesActions();
  useEffect(() => {
    return () => {
      dismissRecentlySavedSearch();
    };
  }, [dismissRecentlySavedSearch]);

  return (
    <SearchResultsBody>
      <SearchPageTitle>
        {LISTING_TYPE_PLURAL_HUMAN_NAME_MAP_TITLE(i18n)[searchType]}
      </SearchPageTitle>

      <SearchResultsHeader
        results={results}
        showSortOptions={showSortOptions}
        searchType={searchType}
        sort={sort}
        saveSearch={saveSearch}
        onChangeSort={setSearchUrlSort}
      />

      <SearchResultsHits
        results={results}
        searchType={searchType}
        searchLocation={searchLocation}
        saveSearch={saveSearch}
        getSearchTrackingData={getSearchTrackingData}
        onChangePageIndex={setSearchUrlPageIndex}
        searchSavedListingsStore={searchSavedListingsStore}
      />
    </SearchResultsBody>
  );
}
